import React, { Fragment } from "react";
import styled from "styled-components";
import PropTypes, { shape } from "prop-types";
import AddModuleButton from "../atoms/add-module-button";
import ConfigurationInformations from "./configuration_informations";
import ModuleSettings from "./module_settings";
import LoadingSpinner from "../atoms/loading";
import DimensionRule from "./dimension-rule";
import PowerSupplyBusFragment from "./power_supply_bus_fragment";
import {
  front_base_url,
  configurationModuleType,
  possible_starter_selections,
  busExpansionType,
  rangeProductType,
  zelio_extension,
  configurationStatuses,
  m172_ranges,
  m172_battery_backup,
  pacdrive_ranges,
  tm172sig_type,
  tm171amb,
  tm172sig,
  m171_need_amb,
  tm171_communication_module_type,
  m171_ranges,
} from "../constants";
import Configuration from "../models/Configuration";
import Device from "../models/Device";
import I10n from "../features/i10n";
import { formatLengthUnit } from "../utils/data-formatter";
import { appConfiguration } from "../utils/app-starter";
import MsxIcons from "../features/msx-icons";
import ConfigurationActionButton from "../atoms/configuration-action-button";
import DropdownMenu from "./dropdown_menu";
import { PRIMARY_COLOR, SECONDARY_COLOR } from "../color-constants";
import BackToList from "./button_back_to_list";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const InlineRow = styled.div`
  position: relative;
  display: inline-block;
  ${(props) =>
    props.paddingLeft ? `padding-left: ${props.paddingLeft}px;` : ""}
  ${(props) =>
    props.paddingRight ? `padding-right: ${props.paddingRight}px;` : ""}
`;

const ConfigurationContainer = styled(Row)`
  display: flex;
  flex-direction: row;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const GraphicalViewWrapper = styled.div`
  display: inline-block;
  white-space: nowrap;
  flex: 3 1 auto;
  position: relative;
  overflow-y: hidden;
  background-color: #fafafa;
  box-shadow: inset 0 0 15px 1px rgba(0, 0, 0, 0.1);
`;

const GraphicalView = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-direction: row;
  margin: 0;
  padding: ${props => props.showIsland ? 25 : 55}px 5px
    ${(props) =>
      props.renderPowerSupplyBuses
        ? `40px`
        : props.canShowWiring
        ? "50px"
        : "40px"}
    5px;
  overflow-y: hidden;
  overflow-x: auto;
  border-radius: 2px;
`;

const GraphicalViewItem = styled.div`
  position: relative;
  height: 100%;
`;

const ButtonContainer = styled.div`
  display: inline-block;
  height: 160px;
  margin: 0 5px;
`;

const ButtonWrapper = styled.div`
  height: ${(props) => (props.height ? `${props.height}px` : "100%")};
`;

const LoadingSpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
  padding: 10px;
`;

const ModuleItemWrapper = styled(GraphicalViewItem)`
  display: inline-block;
  height: 160px;
`;

const ModuleItem = styled.div`
  display: inline-block;
  height: 160px;
  z-index: 1;
  position: relative;
`;

const CableItem = styled(GraphicalViewItem)`
  display: inline-block;
  vertical-align: top;
  height: 160px;
`;

const Cable = styled.div`
  position: relative;
  width: ${(props) => (props.size === "small" ? "35px" : "70px")};
  height: 5px;
  border-radius: 2px;
  margin: -10px -10px 0;
  background-color: ${PRIMARY_COLOR};
  background-color: var(--se-secondary, ${PRIMARY_COLOR});
  z-index: 2;
  ${(props) => (props.position === "top" ? "margin-top: 25px;" : "top: 50%;")}
`;

const DeviceImageWrapper = styled.label`
  display: inline-block;
  position: relative;
  cursor: pointer;
  vertical-align: top;

  :after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    cursor: pointer;
    box-sizing: border-box;

    ${(props) =>
      props.valid
        ? `
      border: 2px solid transparent;
      border-radius: 2px;
      ${props.selected ? "background-color: rgba(66, 180, 230, 0.2);" : ``}
    `
        : `
      background: repeating-linear-gradient(
        -55deg,
        rgba(230, 66, 66, 0.2),
        rgba(230, 66, 66, 0.2) 10px,
        rgba(230, 66, 66, 0.7) 10px,
        rgba(230, 66, 66, 0.7) 14px
      );
    `}

    ${(props) =>
      props.selected &&
      `
      border-color: ${PRIMARY_COLOR};
    `}
  }

  :hover {
    :after {
      border-color: ${PRIMARY_COLOR};
      ${(props) =>
        props.valid
          ? `
        background-color: rgba(66, 180, 230, 0.2);
      `
          : `
        background: repeating-linear-gradient(
          -55deg,
          rgba(66, 180, 230, 0.2),
          rgba(66, 180, 230, 0.2) 10px,
          rgba(230, 66, 66, 0.7) 10px,
          rgba(230, 66, 66, 0.7) 14px
        );
      `}
    }
  }
`;

const DeviceImage = styled.img`
  display: block;
  height: ${(props) => `${props.height}px`};
  width: ${(props) => `${props.width}px`};
  font-size: 8px;
  writing-mode: vertical-lr;
  text-orientation: upright;
  text-align: center;
`;
const SlottedDeviceImage = styled(DeviceImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;
const CartridgeImage = styled(DeviceImage)`
  position: absolute;
  top: 0;
  left: 0;
`;

const ConfigurationInformationsContainer = styled.div`
background-color: white;
  flex: 1 0 300px;
  border: 1px solid #e7e6e6;
  @media (max-width: 800px) {
    flex: 1 0 auto;
  }
`;

const BlockWrapper = styled.div`
  display: inline-block;
`;

const BlockWithDimension = styled.div`
  position: relative;
  display: inline-block;
  flex-direction: column;
`;

const BlockModulesWrapper = styled.div`
  display: inline-block;
`;

const ModuleWithBusWrapper = styled.div`
  display: inline-block;
  flex-direction: column;
`;

const M172WireStart = styled.div`
  box-sizing: content-box;
  position: absolute;
  width: ${(props) => props.width}px;
  height: 100px;
  margin-left: 50%;

  ${(props) =>
    props.isRS485
      ? `
    bottom: -35px;
    ${
      props.forCom
        ? `
      border-right: 5px solid ${SECONDARY_COLOR};
      border-right: 5px solid var(--se-primary, ${SECONDARY_COLOR});
      margin-left: 0px;
    `
        : `
      border-left: 5px solid ${SECONDARY_COLOR};
      border-left: 5px solid var(--se-primary, ${SECONDARY_COLOR});
    `
    }
    border-bottom: 5px solid ${SECONDARY_COLOR};
    border-bottom: 5px solid var(--se-primary, ${SECONDARY_COLOR});
    `
      : `bottom: -15px;
    border-left: 5px solid ${PRIMARY_COLOR};
    border-left: 5px solid var(--se-secondary, ${PRIMARY_COLOR});
    border-bottom: 5px solid ${PRIMARY_COLOR};
    border-bottom: 5px solid var(--se-secondary, ${PRIMARY_COLOR});
    `}
`;

const M172WireLabel = styled.div`
  position: absolute;
  bottom: -2px;
  left: 0px;
  font-size: 12px;
`;

const M172WireMiddleWrapper = styled.div`
  box-sizing: content-box;
  position: absolute;
  width: ${(props) => props.width}px;
  ${(props) => (props.isRS485 ? `bottom: -35px;` : `bottom: -15px;`)}
`;

const M172WireLineH = styled.div`
  box-sizing: content-box;
  width: ${(props) => props.width}px;
  height: 15px;
  ${(props) =>
    props.isRS485
      ? `
    border-bottom: 5px solid ${PRIMARY_COLOR};
    border-bottom: 5px solid var(--se-primary, ${PRIMARY_COLOR});
    `
      : `
    border-bottom: 5px solid ${PRIMARY_COLOR};
    border-bottom: 5px solid var(--se-secondary, ${PRIMARY_COLOR});
    `}
`;

const M172WireLineV = styled.div`
  box-sizing: content-box;
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 5px;
  ${(props) =>
    props.isRS485
      ? `
    background-color: ${SECONDARY_COLOR};
    background-color: var(--se-primary, ${SECONDARY_COLOR});
    height: ${props.height + 20}px;
    `
      : `
    background-color: ${PRIMARY_COLOR};
    background-color: var(--se-secondary, ${PRIMARY_COLOR});
    height: ${props.height}px;
    `}
`;

const M172WireEnd = styled.div`
  box-sizing: content-box;
  position: absolute;
  width: 50%;
  height: 100px;

  ${(props) =>
    props.isRS485
      ? `
    ${
      props.forCom
        ? `
      border-left: 5px solid ${SECONDARY_COLOR};
      border-left: 5px solid var(--se-primary, ${SECONDARY_COLOR});
      margin-left: 50%;
      width: calc(50% + 10px);
    `
        : `
      border-right: 5px solid ${SECONDARY_COLOR};
      border-right: 5px solid var(--se-primary, ${SECONDARY_COLOR});
    `
    }

    border-bottom: 5px solid ${SECONDARY_COLOR};
    border-bottom: 5px solid var(--se-primary, ${SECONDARY_COLOR});
    bottom: -35px;
    `
      : `
    border-right: 5px solid ${PRIMARY_COLOR};
    border-right: 5px solid var(--se-secondary, ${PRIMARY_COLOR});
    border-bottom: 5px solid ${PRIMARY_COLOR};
    border-bottom: 5px solid var(--se-secondary, ${PRIMARY_COLOR});
    bottom: -15px;
    `}
`;

const ActionsContainer = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  align-items: center;
`;

const IoTypeButtonWrapper = styled.div`
  margin-right: 5px;
  font-weight: lighter;
`;

const ModuleAndHintWrapper = styled.div`
  position: relative;
`;

const TooltipWrapper = styled.div`
  position: absolute;
  z-index: 3;
  top: -10px;
  left: 0;
  right: 0;
`;

const TooltipText = styled.span`
  position: absolute;
  color: white;
  background-color: #323232;
  top: -20px;
  padding: 5px 10px;
  font-size: 12px;
  border-radius: 2px;
`;

const TooltipTriangle = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 10px 5px 0 5px;
  border-color: #323232 transparent transparent transparent;
  margin-left: calc(50% - 5px);
`;

const ModuleActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  min-width: 200px;
`;

const ModuleActionRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 10px;
  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});
  :hover {
    background-color: rgb(237, 237, 237);
  }
`;

const InsertButtonWrapper = styled.div`
  display: inline-flex;
  position: relative;
  z-index: 1;
  margin-left: 10px;
`;

const RemoveActionRow = styled(ModuleActionRow)`
  color: #dc0a0a;
`;

const ModuleActionText = styled.span`
  font-size: 16px;
  font-weight: 300;
  margin-left: 10px;
`;

const HeaderIslandButtonWrapper = styled.div`
  padding: 5px;
`;

const BackTolListWrapper = styled.div`
  display: flex;
`;

class ConfigurationPreview extends React.Component {
  static propTypes = {
    configuration: shape(Configuration),
    openModal: PropTypes.func,
    closeModal: PropTypes.func,
    addModuleAccessory: PropTypes.func,
    addModuleAuxiliary: PropTypes.func,
    addModuleSensor: PropTypes.func,
    setModuleWarranty: PropTypes.func,
    addDevices: PropTypes.func,
    addComModules: PropTypes.func,
    addCartridge: PropTypes.func,
    getAccessories: PropTypes.func,
    getModuleActions: PropTypes.func,
    changeSoftwareVersion: PropTypes.func,
    removeCartridge: PropTypes.func,
    duplicateModule: PropTypes.func,
    removeModule: PropTypes.func,
    replaceSelectedAdditionalDevice: PropTypes.func,
    replaceModule: PropTypes.func,
    updateModule: PropTypes.func,
    insertModulesBefore: PropTypes.func,
    insertModulesAfter: PropTypes.func,
    comModulesCategories: PropTypes.array,
    ioModulesCategories: PropTypes.array,
    ioModulesRemainingCriteria: PropTypes.object,
    comModulesRemainingCriteria: PropTypes.object,
    ioModulesSharedOptions: PropTypes.object,
    comModulesSharedOptions: PropTypes.object,
    cartridgesCategories: PropTypes.array,
    comModulesFailedRules: PropTypes.array,
    ioModulesFailedRules: PropTypes.array,
    cartridgesFailedRules: PropTypes.array,
    ioModulesDevicesFailedRules: PropTypes.object,
    comModulesDevicesFailedRules: PropTypes.object,
    cartridgesDevicesFailedRules: PropTypes.object,
    isLoading: PropTypes.bool,
    moduleActionsLoading: PropTypes.bool,
    accessoriesLoading: PropTypes.bool,
    canReplaceHead: PropTypes.bool,
    onAdjustRequested: PropTypes.func,
    pacdriveKits: PropTypes.object,
    allowSavedModifications: PropTypes.bool,
    showIoTypes: PropTypes.func,
    previewConfiguration: PropTypes.func,
    hasPowerSupplies: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      slotModuleSelected: null,
      typeModuleSelected: null,
      renderPowerSupplyBuses: false,
      renderDimensions: false,
      selectedModuleCandidateAccessories: {},
      selectedModuleActions: {},
    };
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  updateStateIfMounted(state) {
    if (this._isMounted) {
      this.setState(state);
    }
  }

  get selectedModule() {
    return this.getModuleAtSlot(
      this.state.slotModuleSelected,
      this.state.typeModuleSelected
    );
  }

  getModuleAtSlot = (slot, moduleType) => {
    let mod;
    switch (moduleType) {
      case configurationModuleType.head_module:
        mod = this.props.configuration.headModule;
        break;
      case configurationModuleType.com_module:
        mod = this.props.configuration.comModules.find((m) => m.slot === slot);
        break;
      case configurationModuleType.io_module:
        mod = this.props.configuration.ioModules.find((m) => m.slot === slot);
        break;
      default:
        break;
    }
    return new Device(mod);
  };

  get canTooltipAppears() {
    try {
      return localStorage.getItem("tooltip-head-occurs") == null;
    } catch (error) {
      return true;
    }
  }

  refreshSelectedModuleActions = (newSlotSelected, newSelectedType) => {
    if (newSlotSelected !== null && newSelectedType !== null) {
      this.props
        .getModuleActions(newSlotSelected, newSelectedType, true)
        .then((result) => {
          if (result && !result.cancelled) {
            this.updateStateIfMounted({ selectedModuleActions: result });
          }
        })
        .catch((error) => {
          throw error;
        });
    }
  };

  updateSelectedModule = (newSlotSelected, newSelectedType) => {
    if (
      newSlotSelected !== this.state.slotModuleSelected ||
      newSelectedType !== this.state.typeModuleSelected
    ) {
      this.updateStateIfMounted({
        slotModuleSelected: newSlotSelected,
        typeModuleSelected: newSelectedType,
        selectedModuleCandidateAccessories: {},
        moduleActions: {},
      });
      if (newSlotSelected !== null) {
        const modSelected = this.getModuleAtSlot(
          newSlotSelected,
          newSelectedType
        );
        if (modSelected) {
          this.refreshSelectedModuleActions(newSlotSelected, newSelectedType);
        }
      }
    }
  };

  renderPacdriveWiring = (
    device,
    moduleType,
    imageHeight,
    imageWidth,
    lastModuleSlot
  ) => {
    return (
      <>
        {this.canShowPacdriveWiring() &&
          this.props.configuration.ioModules.length > 0 && (
            <>
              {moduleType === configurationModuleType.head_module && (
                <>
                  {
                    <M172WireStart
                      isRS485={false}
                      width={imageWidth / 2.0 + 20}
                    >
                      <M172WireLabel>
                        {I10n.get("screen.wiring.pacnet")}
                      </M172WireLabel>
                    </M172WireStart>
                  }
                </>
              )}
              {moduleType === configurationModuleType.io_module && (
                <>
                  {device.slot < lastModuleSlot && (
                    <M172WireMiddleWrapper
                      isRS485={false}
                      width={imageWidth + 10}
                    >
                      <M172WireLineH
                        isRS485={false}
                        width={imageWidth + 10}
                      ></M172WireLineH>
                      {
                        <M172WireLineV
                          isRS485={false}
                          height={30 + (160 - imageHeight)}
                        ></M172WireLineV>
                      }
                    </M172WireMiddleWrapper>
                  )}
                  {device.slot === lastModuleSlot && (
                    <M172WireEnd forCom={false} isRS485={false}></M172WireEnd>
                  )}
                </>
              )}
            </>
          )}
      </>
    );
  };

  renderM172SIGWiring = (
    device,
    moduleType,
    productType,
    imageHeight,
    imageWidth,
    lastM172SIGModuleSlot
  ) => {
    return (
      <>
        {moduleType === configurationModuleType.head_module && (
            <M172WireStart
              forCom={true}
              isRS485={true}
              width={imageWidth / 2.0}
            ></M172WireStart>
          )}
        {moduleType === configurationModuleType.com_module && (
          <>
            {device.slot < lastM172SIGModuleSlot && (
                <M172WireMiddleWrapper isRS485={true} width={imageWidth}>
                  <M172WireLineH
                    isRS485={true}
                    width={imageWidth + 20}
                  ></M172WireLineH>

                  {tm171_communication_module_type === productType && <M172WireLineV
                          isRS485={true}
                          height={30 + (160 - imageHeight)}
                    ></M172WireLineV>}
                </M172WireMiddleWrapper>
              )}
            {tm171_communication_module_type !== productType &&
              device.slot === lastM172SIGModuleSlot && (
                <M172WireEnd forCom={true} isRS485={true}>
                  <M172WireLabel>
                    {I10n.get("screen.wiring.rs485")}
                  </M172WireLabel>
                </M172WireEnd>
              )}
          </>
        )}
      </>
    );
  };

  renderM172Wiring = (
    device,
    moduleType,
    productType,
    imageHeight,
    imageWidth,
    lastM172RS485ModuleSlot,
    lastNonM172RS485ModuleSlot,
    lastM172SIGModuleSlot
  ) => {
    return (
      <>
        {this.canShowM172Wiring() &&
          this.props.configuration.ioModules.length > 0 && (
            <>
              {moduleType === configurationModuleType.head_module && (
                <>
                  {lastM172RS485ModuleSlot > -1 && (
                    <M172WireStart
                      forCom={false}
                      isRS485={true}
                      width={imageWidth / 2.0 + 20}
                    >
                      {(lastM172SIGModuleSlot === -1 || m171_need_amb === productType) && (
                        <M172WireLabel>
                          {I10n.get("screen.wiring.rs485")}
                        </M172WireLabel>
                      )}
                    </M172WireStart>
                  )}
                  {lastNonM172RS485ModuleSlot > -1 && (
                    <M172WireStart
                      forCom={false}
                      isRS485={false}
                      width={imageWidth / 2.0 + 20}
                    >
                      <M172WireLabel>
                        {m171_ranges.includes(device.rangeOfProduct) ? I10n.get("screen.wiring.lan") :  I10n.get("screen.wiring.canbus")}
                      </M172WireLabel>
                    </M172WireStart>
                  )}
                </>
              )}
              {moduleType === configurationModuleType.io_module && (
                <>
                  {device.slot < lastNonM172RS485ModuleSlot && (
                    <M172WireMiddleWrapper
                      isRS485={false}
                      width={imageWidth + 10}
                    >
                      <M172WireLineH
                        isRS485={false}
                        width={imageWidth + 10}
                      ></M172WireLineH>
                      {productType !== m172_battery_backup &&
                        !device.isM172RS485Module() && (
                          <M172WireLineV
                            isRS485={false}
                            height={30 + (160 - imageHeight)}
                          ></M172WireLineV>
                        )}
                    </M172WireMiddleWrapper>
                  )}
                  {device.slot < lastM172RS485ModuleSlot && (
                    <M172WireMiddleWrapper
                      isRS485={true}
                      width={imageWidth + 10}
                    >
                      <M172WireLineH
                        isRS485={true}
                        width={imageWidth + 10}
                      ></M172WireLineH>
                      {productType !== m172_battery_backup &&
                        device.isM172RS485Module() && (
                          <M172WireLineV
                            isRS485={true}
                            height={30 + (160 - imageHeight)}
                          ></M172WireLineV>
                        )}
                    </M172WireMiddleWrapper>
                  )}
                  {(device.slot === lastM172RS485ModuleSlot ||
                    device.slot === lastNonM172RS485ModuleSlot) && (
                    <M172WireEnd
                      forCom={false}
                      isRS485={device.slot === lastM172RS485ModuleSlot}
                    ></M172WireEnd>
                  )}
                </>
              )}
            </>
          )}
      </>
    );
  };

  renderModuleActions = (baseId) => {
    return this.props.moduleActionsLoading || this.props.isLoading ? (
      <LoadingSpinnerWrapper>
        <LoadingSpinner></LoadingSpinner>
      </LoadingSpinnerWrapper>
    ) : (
      <>
        {configurationModuleType.head_module ===
        this.state.typeModuleSelected ? (
          <>
            {this.props.canReplaceHead && (
              <ModuleActionRow
                onClick={() => {
                  this.handleReplaceRequested(false);
                }}
              >
                <MsxIcons size="nano" name="replace"></MsxIcons>
                <ModuleActionText>
                  {I10n.get("module_setting.action.replace")}
                </ModuleActionText>
              </ModuleActionRow>
            )}
            {this.canAdjust(this.selectedModule) && (
              <ModuleActionRow
                onClick={() => {
                  this.props.onAdjustRequested(this.selectedModule);
                }}
              >
                <MsxIcons size="nano" name="pacdrive-adjust"></MsxIcons>
                <ModuleActionText>
                  {I10n.get("module_setting.action.adjust")}
                </ModuleActionText>
              </ModuleActionRow>
            )}
          </>
        ) : (
          <>
            {this.canReplace(this.selectedModule) && (
              <ModuleActionRow
                onClick={() => {
                  this.handleReplaceRequested(false);
                }}
              >
                <MsxIcons size="nano" name="replace"></MsxIcons>
                <ModuleActionText>
                  {I10n.get("module_setting.action.replace")}
                </ModuleActionText>
              </ModuleActionRow>
            )}
            {this.canDuplicate(this.selectedModule) && (
              <ModuleActionRow
                onClick={() => {
                  this.handleDuplicate(false);
                }}
              >
                <MsxIcons size="nano" name="copy"></MsxIcons>
                <ModuleActionText>
                  {I10n.get("module_setting.action.duplicate")}{" "}
                </ModuleActionText>
              </ModuleActionRow>
            )}
            {this.canInsertLeft(this.selectedModule) && (
              <ModuleActionRow
                onClick={() => {
                  this.onInsertModuleLeftRequested(false);
                }}
              >
                <MsxIcons size="nano" name="add"></MsxIcons>
                <ModuleActionText>
                  {I10n.get("module_setting.action.insert_left")}
                </ModuleActionText>
              </ModuleActionRow>
            )}
            {this.canInsertRight(this.selectedModule) && (
              <ModuleActionRow
                onClick={() => {
                  this.onInsertModuleRightRequested(false);
                }}
              >
                <MsxIcons size="nano" name="add"></MsxIcons>
                <ModuleActionText>
                  {I10n.get("module_setting.action.insert_right")}
                </ModuleActionText>
              </ModuleActionRow>
            )}
            {this.canRemove(this.selectedModule) && (
              <RemoveActionRow
                id={`${baseId}-context-menu-remove-module`}
                onClick={() => {
                  this.handleRemove();
                }}
              >
                <MsxIcons size="nano" name="delete"></MsxIcons>
                <ModuleActionText>
                  {I10n.get("module_setting.action.remove")}
                </ModuleActionText>
              </RemoveActionRow>
            )}
          </>
        )}
      </>
    );
  };

  renderSIGInsertButton = (slot) => {
    return (
      <InsertButtonWrapper>
        <AddModuleButton
          title={""}
          disabled={this.props.isLoading}
          onClick={(e) => {
            e.preventDefault();
            this.handleDuplicateConfiguration(
              this.props.configuration.status ===
                configurationStatuses.finished,
              () => {
                this.props.openModal({
                  modalType: "insert_module",
                  modalProps: {
                    insertionSlot: slot,
                    incrementSlot: true,
                    moduleType: configurationModuleType.com_module,
                    questionType: configurationModuleType.com_module,
                    onInsert: (referenceId, quantity) => {
                      this.props
                        .insertModulesBefore(
                          slot,
                          configurationModuleType.com_module,
                          referenceId,
                          quantity
                        )
                        .then(() => {
                          const moduleType = configurationModuleType.com_module;
                          this.refreshSelectedModuleActions(slot, moduleType);
                        });
                    },
                    showWarning: true,
                  },
                });
              },
              true
            );
          }}
        >
          +
        </AddModuleButton>
      </InsertButtonWrapper>
    );
  };

  renderModule = (device, maxModuleHeight, cartridges, moduleProps = {}) => {
    const slot = device.slot;
    const moduleType = device.moduleType;
    const realHeight = device.height ? device.height : 0;
    const realWidth = device.width ? device.width : 0;
    const imageHeight = this.computeModuleHeight(realHeight, maxModuleHeight);
    const imageWidth = this.computeModuleWidth(
      realWidth,
      realHeight,
      imageHeight
    );
    const cartridgeDevices = cartridges
      .map((d) => new Device(d))
      .sort((a, b) => a.slot - b.slot);
    const slottedDevicesImages = device.getSlottedDevicesFrontImageLinks();

    const lastModule = this.props.configuration.modules().slice(-1)[0];
    const lastM172RS485ModuleSlot =
      this.props.configuration.findLastM172RS485ModuleSlot();
    const lastNonM172RS485ModuleSlot =
      this.props.configuration.findLastNonM172RS485ModuleSlot();
    const lastM172SIGModuleSlot =
      this.props.configuration.findLastM172SIGModuleSlot();

    const shouldRenderSIGInsertButton =
      moduleType === configurationModuleType.com_module &&
      lastM172SIGModuleSlot !== -1 &&
      device.id === tm172sig &&
      (this.props.configuration.comModules || []).length <
        this.props.configuration?.rangeInfo?.maxComModules;

    const ModuleRender = (
      <>
        <ModuleItem>
          <DeviceImageWrapper
            id={`${device.id}-${slot}-module-image-wrapper`}
            htmlFor={`${device.id}_${slot}`}
            valid={device.validator.valid}
            selected={
              moduleType === this.state.typeModuleSelected &&
              slot === this.state.slotModuleSelected
            }
            onClick={() => {
              this.updateSelectedModule(slot, moduleType);
              try {
                localStorage.setItem("tooltip-head-occurs", "1");
              } catch (error) {}
            }}
            onContextMenu={() => {
              this.updateSelectedModule(slot, moduleType);
              try {
                localStorage.setItem("tooltip-head-occurs", "1");
              } catch (error) {}
            }}
          >
            <DeviceImage
              src={`${front_base_url}/images/small-devices-images/${device.id}_h320px.png`}
              alt={`${device.id} module front view`}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = device.sidePicture;
              }}
              height={imageHeight}
              width={imageWidth}
            ></DeviceImage>
            {(!(configurationModuleType.head_module === moduleType) && ![...m171_ranges, ...m172_ranges].includes(device?.rangeOfProduct)) &&
              slottedDevicesImages.map((imgObj, i) => {
                return (
                  <SlottedDeviceImage
                    key={i}
                    src={`${front_base_url}/images/small-devices-images/${imgObj.src}_h320px.png`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = imgObj.fallback;
                    }}
                    alt={`${imgObj.id} module front view`}
                    height={imageHeight}
                    width={imageWidth}
                  ></SlottedDeviceImage>
                );
              })}
            {cartridgeDevices.map((d, i) => {
              return (
                <CartridgeImage
                  key={i}
                  src={`${front_base_url}${device.baseCartridgeURL}_${d.id}_Slot${d.slot}.png`}
                  alt={`${d.id} module front view`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = d.sidePicture;
                  }}
                  height={imageHeight}
                  width={imageWidth}
                ></CartridgeImage>
              );
            })}
          </DeviceImageWrapper>
        </ModuleItem>
        {shouldRenderSIGInsertButton && this.renderSIGInsertButton(slot)}
        {lastM172SIGModuleSlot !== -1 &&
          this.renderM172SIGWiring(
            device,
            moduleType,
            device.productType,
            imageHeight,
            imageWidth,
            lastM172SIGModuleSlot
          )}
        {this.renderM172Wiring(
          device,
          moduleType,
          device.productType,
          imageHeight,
          imageWidth,
          lastM172RS485ModuleSlot,
          lastNonM172RS485ModuleSlot,
          lastM172SIGModuleSlot
        )}
        {this.renderPacdriveWiring(
          device,
          moduleType,
          imageHeight,
          imageWidth,
          lastModule.slot
        )}
      </>
    );

    return (
      <ModuleItemWrapper key={`${device.id}_${slot}`}>
        {
          <DropdownMenu
            data-clickable
            fixedTop={20}
            openOnRightClick={true}
            toggleElement={<>{ModuleRender}</>}
            boundingElement={this.wrapperElement}
            containerElement={
              <ModuleActionsWrapper>
                {this.renderModuleActions(`${device.id}-${slot}`)}
              </ModuleActionsWrapper>
            }
          ></DropdownMenu>
        }
      </ModuleItemWrapper>
    );
  };

  renderHeadModule = (maxModuleHeight) => {
    const device = this.props.configuration.headModule;
    const cartridges = this.props.configuration.cartridges;

    const moduleProps = { item: {}, image: {} };
    return (
      <ModuleAndHintWrapper>
        {this.renderModule(device, maxModuleHeight, cartridges, moduleProps)}
        {this.canTooltipAppears && (
          <TooltipWrapper>
            <TooltipText>
              {I10n.get("screen.config_editor.preview.tooltip")}
            </TooltipText>
            <TooltipTriangle></TooltipTriangle>
          </TooltipWrapper>
        )}
      </ModuleAndHintWrapper>
    );
  };

  renderPowerSupplyBus = (module, configuration) => {
    const [bus, busIdx] = configuration.findPowerSupplyBusOfModule(module);
    return bus ? (
      <PowerSupplyBusFragment
        busName={I10n.getAndReplace(
          "configuration.general.consumption.tm5.5v",
          busIdx
        )}
        busLength={bus?.length()}
        isBusHeadModule={bus?.isFirstModule(module)}
        isBusLastModule={bus?.isLastModule(module)}
      ></PowerSupplyBusFragment>
    ) : null;
  };

  renderPowerSupplyBusSegment = (module, configuration) => {
    const [busSegment, nameSuffix] =
      configuration.findPowerSupplyBusSegmentOfModule(module);
    return busSegment ? (
      <PowerSupplyBusFragment
        busName={I10n.getAndReplace(
          "configuration.general.consumption.tm5.24v",
          nameSuffix
        )}
        busLength={busSegment?.length()}
        isBusHeadModule={busSegment?.isFirstModule(module)}
        isBusLastModule={busSegment?.isLastModule(module)}
      ></PowerSupplyBusFragment>
    ) : null;
  };

  renderByBlocks = (maxModuleHeight) => {
    const configuration = this.props.configuration;
    const blocks = configuration.blocks();
    return blocks.map((block, idx) => {
      return (
        <BlockWrapper key={idx}>
          {idx > 0 && this.renderCable()}
          <BlockWithDimension>
            {this.state.renderDimensions && block.hasWidth() && (
              <DimensionRule
                text={formatLengthUnit(block.width())}
                orientation={"horizontal"}
              ></DimensionRule>
            )}
            <BlockModulesWrapper>
              {block.modules.map((module, idx) => {
                const isBoth =
                  busExpansionType.both === module.busExpansionType;
                return (
                  <ModuleWithBusWrapper key={idx}>
                    <InlineRow
                      paddingRight={
                        tm172sig_type === module.productType ? 10 : 0
                      }
                      paddingLeft={
                        (this.canShowM172Wiring() ||
                          this.canShowPacdriveWiring()) &&
                        module.moduleType ===
                          configurationModuleType.io_module &&
                        !(module.productType === m172_battery_backup)
                          ? module.slot === 1
                            ? 20
                            : 10
                          : 0
                      }
                    >
                      {isBoth && idx > 0 && this.renderCable("top", "small")}
                      {module.moduleType === configurationModuleType.head_module
                        ? this.renderHeadModule(maxModuleHeight)
                        : this.renderModule(module, maxModuleHeight, [])}
                      {zelio_extension === module.productType &&
                        this.renderCable("center", "small")}
                    </InlineRow>
                    {this.state.renderPowerSupplyBuses &&
                      configuration.rangeInfo.canShowPowerSupplyBuses() && (
                        <Fragment>
                          {this.renderPowerSupplyBus(module, configuration)}
                          {this.renderPowerSupplyBusSegment(
                            module,
                            configuration
                          )}
                        </Fragment>
                      )}
                  </ModuleWithBusWrapper>
                );
              })}
            </BlockModulesWrapper>
          </BlockWithDimension>
        </BlockWrapper>
      );
    });
  };

  renderCable = (position = "center", size = "medium") => {
    const cableProps = {
      position,
      size,
    };
    return (
      <CableItem {...cableProps}>
        <Cable {...cableProps}></Cable>
      </CableItem>
    );
  };

  handleDuplicateConfiguration = (
    isSaved,
    callback,
    promptClearPowerSupply = false
  ) => {
    if (isSaved && !this.props.allowSavedModifications) {
      const modalTitle = promptClearPowerSupply
        ? "modal.change_saved_configuration_with_power_supply.title"
        : "modal.change_saved_configuration.title";
      const modalBody = promptClearPowerSupply
        ? "modal.change_saved_configuration_with_power_supply.body"
        : "modal.change_saved_configuration.body";

      this.props.openModal({
        modalType: "confirmation",
        modalProps: {
          onCancel: () => {
            this.props.closeModal();
          },
          onSubmit: () => {
            this.props.closeModal();
            callback();
          },
          title: I10n.get(modalTitle),
          text: I10n.get(modalBody),
          cancelText: I10n.get("app.action.cancel"),
          submitText: I10n.get("app.action.continue"),
        },
      });
    } else {
      if (this.props.hasPowerSupplies && promptClearPowerSupply) {
        this.props.openModal({
          modalType: "confirmation",
          modalProps: {
            onCancel: () => {
              this.props.closeModal();
            },
            onSubmit: () => {
              this.props.closeModal();
              callback();
            },
            title: I10n.get("screen.reset_power_supply.modal.title"),
            text: I10n.get("screen.reset_power_supply.modal.body"),
            cancelText: I10n.get("app.action.cancel"),
            submitText: I10n.get("app.action.continue"),
          },
        });
      } else {
        callback();
      }
    }
  };

  renderAddComButton = (maxModuleHeight, isSaved) => {
    const isDisabled =
      this.props.comModulesCategories.filter(
        (c) => (c?.references || []).length
      ).length === 0;
    const cmp = (
      <AddModuleButton
        id="add-com-button"
        title={this.buildFailedRulesTooltip("COM")}
        disabled={isDisabled}
        onClick={(e) => {
          e.preventDefault();
          this.handleDuplicateConfiguration(
            isSaved,
            () => {
              this.props.openModal({
                modalType: "add_module",
                modalProps: {
                  addDevices: this.handleAddComModules,
                  categories: this.props.comModulesCategories,
                  remainingCriteria: this.props.comModulesRemainingCriteria,
                  sharedOptions: this.props.comModulesSharedOptions,
                  moduleType: configurationModuleType.com_module,
                  questionType: configurationModuleType.com_module,
                },
              });
            },
            true
          );
        }}
      >
        +
      </AddModuleButton>
    );

    const lastDevice = this.props.configuration.comModules.reduce(
      (previous, current) =>
        current.slot > previous.slot ? current : previous,
      new Device({})
    );

    return this.renderAddButton(cmp, lastDevice, maxModuleHeight);
  };

  renderAddIOButton = (maxModuleHeight, isSaved) => {
    const isDisabled =
      this.props.ioModulesCategories.filter((c) => (c?.references || []).length)
        .length === 0;
    const cmp = (
      <AddModuleButton
        id='add-io-button'
        title={this.buildFailedRulesTooltip("IO")}
        disabled={isDisabled}
        onClick={(e) => {
          e.preventDefault();
          this.handleDuplicateConfiguration(
            isSaved,
            () => {
              this.props.openModal({
                modalType: "add_module",
                modalProps: {
                  addDevices: this.handleAddIOModules,
                  categories: this.props.ioModulesCategories,
                  remainingCriteria: this.props.ioModulesRemainingCriteria,
                  sharedOptions: this.props.ioModulesSharedOptions,
                  moduleType: configurationModuleType.io_module,
                  questionType:
                    this.props.configuration?.rangeInfo
                      ?.ioModulesQuestionCategory,
                },
              });
            },
            true
          );
        }}
      >
        +
      </AddModuleButton>
    );

    const lastDevice = this.props.configuration.ioModules.reduce(
      (previous, current) =>
        current.slot > previous.slot ? current : previous,
      new Device({})
    );

    return this.renderAddButton(cmp, lastDevice, maxModuleHeight);
  };

  /**
   * Render the add button if modules gathering is done.
   */
  renderAddButton = (addBtnComponent, lastDevice, maxModuleHeight) => {
    const realHeight = lastDevice.height ? lastDevice.height : 0;
    const buttonHeight = this.computeModuleHeight(realHeight, maxModuleHeight);
    return (
      <ButtonContainer>
        {this.props.isLoading && (
          <LoadingSpinnerWrapper>
            <LoadingSpinner></LoadingSpinner>
          </LoadingSpinnerWrapper>
        )}
        {!this.props.isLoading && (
          <ButtonWrapper height={buttonHeight}>{addBtnComponent}</ButtonWrapper>
        )}
      </ButtonContainer>
    );
  };

  computeModuleHeight = (realHeight, maxModuleHeight) => {
    const heightRatio = Math.ceil((100 * realHeight) / maxModuleHeight) / 100;
    return 160 * (heightRatio ? heightRatio : 1);
  };

  computeModuleWidth = (realWidth, realHeight, newHeight) => {
    const aspectRatio = realWidth / realHeight;
    return newHeight * (aspectRatio ? aspectRatio.toFixed(2) : 1);
  };

  buildFailedRulesTooltip = (buttonType) => {
    let failedRulesToDisplay = [];
    let failedRulesString = "";
    switch (buttonType) {
      case "IO":
        failedRulesToDisplay = this.props.ioModulesFailedRules;
        break;
      case "COM":
        failedRulesToDisplay = this.props.comModulesFailedRules;
        break;
      default:
        break;
    }

    failedRulesToDisplay.forEach((rule) => {
      failedRulesString += I10n.get(rule.name, "data", { full: true }) + "\n";
    });

    return failedRulesString;
  };

  hasCartridges = () => {
    return (
      this.state.typeModuleSelected === configurationModuleType.head_module
    );
  };

  hasConnectionType = () => {
    return this.selectedModule.relatedConnectionDevices?.length > 0;
  };

  hasFuse = () => {
    return (
      Object.values(this.selectedModule.relatedFuseDevices || {}).length > 0
    );
  };

  hasSlottedAdditionalDevices = () => {
    return this.selectedModule.slottedAdditionalDevices?.length > 0;
  };

  canHandleCartridgeActions = () => {
    return !this.props.isLoading && this.hasCartridges();
  };

  canShowScreenAccessories = (device) => {
    return (
      this.state.typeModuleSelected === configurationModuleType.head_module &&
      m172_ranges.includes(device.rangeOfProduct)
    );
  };

  canShowSparePartAccessories = (device) => {
    return (
      this.state.typeModuleSelected === configurationModuleType.head_module &&
      pacdrive_ranges.includes(device.rangeOfProduct)
    );
  };

  canShowM172Wiring = () => {
    return m172_ranges.includes(this.props.configuration.headModule.rangeOfProduct) || m171_ranges.includes(this.props.configuration.headModule.rangeOfProduct) ;
  };

  canShowPacdriveWiring = () => {
    return pacdrive_ranges.includes(
      this.props.configuration.headModule.rangeOfProduct
    );
  };

  canDuplicate = (device) => {
    let existsInNextModule = false;
    if (this.state.typeModuleSelected === configurationModuleType.io_module) {
      const references = this.props.ioModulesCategories
        .map((cat) => cat.references)
        .flat();
      for (const ref of references) {
        if (ref.id === device.id) {
          existsInNextModule = true;
          break;
        }
      }
    } else if (
      this.state.typeModuleSelected === configurationModuleType.com_module
    ) {
      const references = this.props.comModulesCategories
        .map((cat) => cat.references)
        .flat();
      for (const ref of references) {
        if (ref.id === device.id) {
          existsInNextModule = true;
          break;
        }
      }
    }
    return (
      !this.props.isLoading &&
      !this.props.moduleActionsLoading &&
      existsInNextModule
    );
  };

  canRemove = (device) => {
    return (
      !this.props.isLoading &&
      !this.props.moduleActionsLoading &&
      this.state.typeModuleSelected !== configurationModuleType.head_module &&
      (device.busExpansionType !== busExpansionType.none ||
        device.isPowerSupplier() ||
        (this.state.selectedModuleActions &&
          this.state.selectedModuleActions.canRemove === true))
    );
  };

  canInsertLeft = (device) => {
    if (this.props.isLoading || this.props.moduleActionsLoading) return false;

    if (this.props.configuration.hasMasterValidationError()) return false;

    const comModulesLength = (this.props.configuration.comModules || []).length;
    const ioModulesLength = (this.props.configuration.ioModules || []).length;
    const rangeInfo = this.props.configuration.rangeInfo;

    if (this.state.typeModuleSelected === configurationModuleType.io_module) {
      return (
        !busExpansionType.isReceiver(device.busExpansionType) &&
        rangeInfo.maxTotalIOModules + rangeInfo.maxIOModuleBlocks >
          ioModulesLength &&
        this.state.selectedModuleActions &&
        this.state.selectedModuleActions.canInsertBefore === true
      );
    } else {
      return (
        comModulesLength > 0 &&
        device.slot < comModulesLength &&
        !busExpansionType.isTransmitter(device.busExpansionType) &&
        rangeInfo.maxComModules > comModulesLength &&
        this.state.selectedModuleActions &&
        this.state.selectedModuleActions.canInsertAfter === true
      );
    }
  };

  canInsertRight = (device) => {
    if (this.props.isLoading || this.props.moduleActionsLoading) return false;

    if (this.props.configuration.hasMasterValidationError()) return false;

    const comModulesLength = (this.props.configuration.comModules || []).length;
    const ioModulesLength = (this.props.configuration.ioModules || []).length;
    const rangeInfo = this.props.configuration.rangeInfo;

    if (this.state.typeModuleSelected === configurationModuleType.com_module) {
      return (
        !busExpansionType.isReceiver(device.busExpansionType) &&
        rangeInfo.maxComModules > comModulesLength &&
        this.state.selectedModuleActions &&
        this.state.selectedModuleActions.canInsertBefore === true
      );
    } else {
      return (
        ioModulesLength > 0 &&
        device.slot < ioModulesLength &&
        !busExpansionType.isTransmitter(device.busExpansionType) &&
        rangeInfo.maxTotalIOModules + rangeInfo.maxIOModuleBlocks >
          ioModulesLength &&
        this.state.selectedModuleActions &&
        this.state.selectedModuleActions.canInsertAfter === true
      );
    }
  };

  canReplace = (device) => {
    return (
      !this.props.isLoading &&
      !this.props.moduleActionsLoading &&
      ((device.busExpansionType === busExpansionType.none &&
        this.state.selectedModuleActions &&
        this.state.selectedModuleActions.canReplace === true) ||
        (device.moduleType === configurationModuleType.head_module &&
          this.props.canReplaceHead))
    );
  };

  canAdjust = (device) => {
    const deviceKits = this.props.pacdriveKits[device.id] || {};
    return (
      !this.props.isLoading &&
      !this.props.moduleActionsLoading &&
      Object.keys(deviceKits).length > 0
    );
  };

  canShowSoftwareVersion = (device) => {
    return (
      device.moduleType === configurationModuleType.head_module &&
      this.props.configuration.rangeInfo.productType === rangeProductType.plc &&
      device?.possibleSoftwareVersions?.length > 0
    );
  };

  handleAddIOModules = (referenceID, quantity) => {
    this.props.addDevices(referenceID, quantity).then(() => {
      const slot = this.state.slotModuleSelected;
      const moduleType = this.state.typeModuleSelected;
      this.refreshSelectedModuleActions(slot, moduleType);
    });
  };

  handleAddComModules = (referenceID, quantity) => {
    this.props.addComModules(referenceID, quantity).then(() => {
      const slot = this.state.slotModuleSelected;
      const moduleType = this.state.typeModuleSelected;
      this.refreshSelectedModuleActions(slot, moduleType);
    });
  };

  duplicateModule = (slot, moduleType, closeModuleSettings) => {
    this.props
      .duplicateModule(slot, moduleType)
      .then(() => {
        let newSelectedSlot = null;
        if (moduleType === configurationModuleType.io_module) {
          newSelectedSlot = this.props.configuration.ioModules.reduce(
            (a, b) => (a.slot > b.slot ? a.slot : b.slot),
            0
          );
        } else {
          newSelectedSlot = this.props.configuration.comModules.reduce(
            (a, b) => (a.slot > b.slot ? a.slot : b.slot),
            0
          );
        }
        if (closeModuleSettings) {
          this.updateSelectedModule(null, null);
        } else {
          this.updateSelectedModule(newSelectedSlot, moduleType);
        }
      })
      .catch((error) => {});
  };

  handleDuplicate = (closeModuleSettings = false) => {
    const isSaved =
      this.props.configuration.status === configurationStatuses.finished;
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    this.handleDuplicateConfiguration(
      isSaved,
      () => {
        this.duplicateModule(slot, moduleType, closeModuleSettings);
      },
      true
    );
  };

  formatConnectedIslandsText = (connectedIslands) => {
    let text = "\n\n";
    connectedIslands.forEach((islandWithProtocol) => {
      text += `${islandWithProtocol.islandName} (${islandWithProtocol.protocolName})\n`;
    });
    return text;
  };

  islandsConnectedToModule = (module) => {
    const islandsWithProtocols = [];
    const configurationPLC = this.props.configuration.headModule;
    module.masterProtocols.forEach((protocol) => {
      const protocolName = protocol.name;
      const islandsConnectedWithProtocol =
        this.props.configuration.getIslandsConnectedWithProtocol(protocol.name);

      islandsConnectedWithProtocol.forEach((island) => {
        if (
          configurationPLC.masterProtocols.filter(
            (islandProtocol) => islandProtocol.name !== protocolName
          ).length > 0
        ) {
          islandsWithProtocols.push({
            islandName: island.getName(),
            protocolName,
          });
        }
      });
    });

    return islandsWithProtocols;
  };

  removeModule = () => {
    const islandsConnectedToModule = this.islandsConnectedToModule(
      this.selectedModule
    );
    if (islandsConnectedToModule.length > 0) {
      this.props.openModal({
        modalType: "information",
        modalProps: {
          onButtonClick: () => {
            this.props.closeModal();
          },
          title: "",
          text: I10n.getAndReplace(
            "modal.error.island_attached_to_device.body",
            this.formatConnectedIslandsText(islandsConnectedToModule)
          ),
          buttonText: I10n.get("app.action.close"),
        },
      });
    } else {
      let modalBody = I10n.get("module_setting.remove.modal.body");
      if (this.selectedModule.busExpansionType !== busExpansionType.none) {
        modalBody = I10n.get("module_setting.remove.modal.body_multiple");
      } else if (this.selectedModule.isPowerSupplier()) {
        modalBody = I10n.get("module_setting.remove.modal.body_power_supply");
      }

      // Special case for TM171 SIG
      if (
        this.selectedModule &&
        m171_need_amb === this.props.configuration?.headModule?.productType &&
        this.selectedModule.id === tm171amb &&
        [...this.props.configuration.comModules]
          .map((m) => m.id)
          .includes(tm172sig)
      ) {
        modalBody = I10n.get("screen.config_editor.delete_amb");
      }

      this.props.openModal({
        modalType: "confirmation",
        modalProps: {
          onCancel: () => {
            this.props.closeModal();
          },
          onSubmit: () => {
            this.props.removeModule(
              this.state.slotModuleSelected,
              this.state.typeModuleSelected,
              true
            );
            this.updateSelectedModule(null, null);
            this.props.closeModal();
          },
          title: I10n.get("module_setting.remove.modal.title"),
          text: modalBody,
          cancelText: I10n.get("app.action.cancel"),
          submitText: I10n.get("app.action.confirm"),
        },
      });
    }
  };

  handleRemove = () => {
    const isSaved =
      this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(
      isSaved,
      () => {
        this.removeModule();
      },
      true
    );
  };

  handleRemoveCartridgeRequested = (slot, cartridge) => {
    const isSaved =
      this.props.configuration.status === configurationStatuses.finished;
    const islandsConnectedToCartridge =
      this.islandsConnectedToModule(cartridge);
    this.handleDuplicateConfiguration(
      isSaved,
      () => {
        if (islandsConnectedToCartridge.length > 0) {
          this.props.openModal({
            modalType: "information",
            modalProps: {
              onButtonClick: () => {
                this.props.closeModal();
              },
              title: "",
              text: I10n.getAndReplace(
                "modal.error.island_attached_to_device.body",
                this.formatConnectedIslandsText(islandsConnectedToCartridge)
              ),
              buttonText: I10n.get("app.action.close"),
            },
          });
        } else {
          this.props.removeCartridge(slot);
        }
      },
      true
    );
  };

  _changeSoftwareVersion = (newSoftwareVersion) => {
    this.props.changeSoftwareVersion(newSoftwareVersion);
  };

  handleSoftwareVersionChange = (newSoftwareVersion) => {
    const isSaved =
      this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(
      isSaved,
      () => {
        this._changeSoftwareVersion(newSoftwareVersion);
      },
      false
    );
  };

  replaceModule = (closeModuleSettings) => {
    const moduleType = this.state.typeModuleSelected;
    const islandsConnectedToModule = this.islandsConnectedToModule(
      this.selectedModule
    );
    if (
      islandsConnectedToModule.length > 0 &&
      moduleType !== configurationModuleType.head_module
    ) {
      this.props.openModal({
        modalType: "information",
        modalProps: {
          onButtonClick: () => {
            this.props.closeModal();
          },
          title: "",
          text: I10n.getAndReplace(
            "modal.error.island_attached_to_device.body",
            this.formatConnectedIslandsText(islandsConnectedToModule)
          ),
          buttonText: I10n.get("app.action.close"),
        },
      });
    } else {
      if (moduleType === configurationModuleType.head_module) {
        this.props.openModal({
          modalType: "replace_head",
          modalProps: {
            selectedHead: this.selectedModule,
            onReplaceHead: (moduleId) => {
              this.handleReplace(moduleId, closeModuleSettings);
            },
          },
        });
      } else {
        // Special case for TM171 SIG
        if (
          this.selectedModule &&
          m171_need_amb === this.props.configuration?.headModule?.productType &&
          this.selectedModule.id === tm171amb &&
          [...this.props.configuration.comModules]
            .map((m) => m.id)
            .includes(tm172sig)
        ) {
          this.props.openModal({
            modalType: "confirmation",
            modalProps: {
              onCancel: () => {
                this.props.closeModal();
              },
              onSubmit: () => {
                this.props.openModal({
                  modalType: "replace_module",
                  modalProps: {
                    replacementSlot: this.state.slotModuleSelected,
                    moduleType: this.state.typeModuleSelected,
                    questionType: this.modaleReplaceQuestionType,
                    onReplace: (moduleId) => {
                      this.handleReplace(moduleId, closeModuleSettings);
                    },
                  },
                });
              },
              title: I10n.get("module_setting.remove.modal.title"),
              text: I10n.get("screen.config_editor.replace_amb"),
              cancelText: I10n.get("app.action.cancel"),
              submitText: I10n.get("app.action.confirm"),
            },
          });
          return;
        }
        this.props.openModal({
          modalType: "replace_module",
          modalProps: {
            replacementSlot: this.state.slotModuleSelected,
            moduleType: this.state.typeModuleSelected,
            questionType: this.modaleReplaceQuestionType,
            onReplace: (moduleId) => {
              this.handleReplace(moduleId, closeModuleSettings);
            },
          },
        });
      }
    }
  };

  handleReplaceRequested = (closeModuleSettings = false) => {
    const isSaved =
      this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(
      isSaved,
      () => {
        this.replaceModule(closeModuleSettings);
      },
      true
    );
  };

  handleReplace = (referenceID, closeModuleSettings = false) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;

    return this.props
      .replaceModule(slot, moduleType, referenceID)
      .then((success) => {
        if (closeModuleSettings) {
          this.updateSelectedModule(null, null);
        } else {
          this.refreshSelectedModuleActions(slot, moduleType);
        }
      });
  };

  handleUpdate = (module) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;

    const isSaved =
      this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(
      isSaved,
      () => {
        this.props.updateModule(slot, moduleType, module);
      },
      true
    );
  };

  changeSlottedAdditionalDevice = (layer, selectedDevice) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    this.props.replaceSelectedAdditionalDevice(
      slot,
      moduleType,
      layer,
      selectedDevice
    );
  };

  handleSlottedAdditionalDeviceChange = (layer, selectedDevice) => {
    const isSaved =
      this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(
      isSaved,
      () => {
        this.changeSlottedAdditionalDevice(layer, selectedDevice);
      },
      true
    );
  };

  insertModulesBefore = (
    slot,
    moduleType,
    reference,
    quantity,
    closeModuleSettings
  ) => {
    return this.props
      .insertModulesBefore(slot, moduleType, reference, quantity)
      .then((success) => {
        if (closeModuleSettings) {
          this.updateSelectedModule(null, null);
        } else {
          this.updateSelectedModule(slot + quantity, moduleType);
        }
      })
      .catch((error) => {});
  };

  insertModulesAfter = (
    slot,
    moduleType,
    reference,
    quantity,
    closeModuleSettings
  ) => {
    return this.props
      .insertModulesAfter(slot, moduleType, reference, quantity)
      .then((success) => {
        if (closeModuleSettings) {
          this.updateSelectedModule(null, null);
        } else {
          this.refreshSelectedModuleActions(slot, moduleType);
        }
      })
      .catch((error) => {});
  };

  handleInsertLeft = (reference, quantity, closeModuleSettings = false) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;

    if (moduleType === configurationModuleType.io_module) {
      return this.insertModulesBefore(
        slot,
        moduleType,
        reference,
        quantity,
        closeModuleSettings
      );
    } else if (moduleType === configurationModuleType.com_module) {
      return this.insertModulesAfter(
        slot,
        moduleType,
        reference,
        quantity,
        closeModuleSettings
      );
    } else {
      return this.insertModulesBefore(
        slot,
        configurationModuleType.com_module,
        reference,
        quantity,
        closeModuleSettings
      );
    }
  };

  handleInsertRight = (reference, quantity, closeModuleSettings = false) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;

    if (moduleType === configurationModuleType.io_module) {
      return this.insertModulesAfter(
        slot,
        moduleType,
        reference,
        quantity,
        closeModuleSettings
      );
    } else if (moduleType === configurationModuleType.com_module) {
      return this.insertModulesBefore(
        slot,
        moduleType,
        reference,
        quantity,
        closeModuleSettings
      );
    } else {
      return this.insertModulesBefore(
        slot,
        configurationModuleType.io_module,
        reference,
        quantity,
        closeModuleSettings
      );
    }
  };

  get modaleInsertLeftSlot() {
    const slot = this.state.slotModuleSelected;
    return this.state.typeModuleSelected === configurationModuleType.io_module
      ? slot
      : slot + 1;
  }
  get modaleInsertRightSlot() {
    const slot = this.state.slotModuleSelected;
    return this.state.typeModuleSelected === configurationModuleType.com_module
      ? slot
      : slot + 1;
  }

  get modaleInsertLeftModuleType() {
    const moduleType = this.state.typeModuleSelected;
    return moduleType === configurationModuleType.head_module
      ? configurationModuleType.com_module
      : moduleType;
  }
  get modaleInsertRightModuleType() {
    const moduleType = this.state.typeModuleSelected;
    return moduleType === configurationModuleType.head_module
      ? configurationModuleType.io_module
      : moduleType;
  }

  get modaleInsertLeftQuestionType() {
    const moduleType = this.state.typeModuleSelected;
    return moduleType === configurationModuleType.com_module
      ? configurationModuleType.com_module
      : this.props.configuration?.rangeInfo?.ioModulesQuestionCategory;
  }

  get modaleInsertRightQuestionType() {
    const moduleType = this.state.typeModuleSelected;
    return moduleType === configurationModuleType.com_module
      ? configurationModuleType.com_module
      : this.props.configuration?.rangeInfo?.ioModulesQuestionCategory;
  }

  get modaleReplaceQuestionType() {
    const moduleType = this.state.typeModuleSelected;
    if (moduleType === configurationModuleType.io_module) {
      return this.props.configuration?.rangeInfo?.ioModulesQuestionCategory;
    } else if (moduleType === configurationModuleType.com_module) {
      return configurationModuleType.com_module;
    }

    return "";
  }

  setModuleWarranty = (newWarranty) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    this.props.setModuleWarranty(slot, moduleType, newWarranty);
  };

  addModuleAccessory = (newAccessory) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    this.props.addModuleAccessory(slot, moduleType, newAccessory);
  };

  addModuleAuxiliary = (newAccessory) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    this.props.addModuleAuxiliary(slot, moduleType, newAccessory);
  };

  addModuleSensor = (newAccessory) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    this.props.addModuleSensor(slot, moduleType, newAccessory);
  };

  addModuleSparePart = (newAccessory) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    this.props.addModuleSparePart(slot, moduleType, newAccessory);
  };

  onInsertModuleLeftRequested = (closeModuleSettings) => {
    this.props.openModal({
      modalType: "insert_module",
      modalProps: {
        insertionSlot: this.modaleInsertLeftSlot,
        incrementSlot: true,
        moduleType: this.modaleInsertLeftModuleType,
        questionType: this.modaleInsertLeftQuestionType,
        onInsert: (moduleId, quantity) => {
          this.handleInsertLeft(moduleId, quantity, closeModuleSettings);
        },
        showWarning: false,
      },
    });
  };

  onInsertModuleRightRequested = (closeModuleSettings) => {
    this.props.openModal({
      modalType: "insert_module",
      modalProps: {
        insertionSlot: this.modaleInsertRightSlot,
        incrementSlot: true,
        moduleType: this.modaleInsertRightModuleType,
        questionType: this.modaleInsertRightQuestionType,
        onInsert: (moduleId, quantity) => {
          this.handleInsertRight(moduleId, quantity, closeModuleSettings);
        },
        showWarning: false,
      },
    });
  };

  setWrapperRef = (element) => {
    this.wrapperElement = element;
  };

  render = () => {
    const config = this.props.configuration;
    const maxModuleHeight = config.maxModuleHeight();
    const selectedModule = this.selectedModule;
    const hasSelectedModule =
      !!selectedModule && this.state.slotModuleSelected !== null;
    const isSaved = config.status === configurationStatuses.finished;
    const lastComModule = this.props.configuration.comModules.slice(-1)[0];

    return (
      <Container>
        <ConfigurationContainer>
          <GraphicalViewWrapper>
            <ActionsContainer>
              <IoTypeButtonWrapper>
                <ConfigurationActionButton
                  title={I10n.get("screen.config_editor.preview.io_types")}
                  width={32}
                  height={32}
                  onClick={() => {
                    this.props.showIoTypes();
                  }}
                >
                  {I10n.get("screen.config_editor.preview.io_types.button")}
                </ConfigurationActionButton>
              </IoTypeButtonWrapper>
              <ConfigurationActionButton
                title={I10n.get("screen.config_editor.preview")}
                width={32}
                height={32}
                onClick={() => {
                  this.props.previewConfiguration();
                }}
              >
                <MsxIcons name="expand" size="nano"></MsxIcons>
              </ConfigurationActionButton>
            </ActionsContainer>
            {this.props.isIsland &&
            <BackTolListWrapper>
              <HeaderIslandButtonWrapper>
              <BackToList
                unselectIsland={this.props.unselectIsland}
              />
              </HeaderIslandButtonWrapper>
            </BackTolListWrapper>
  }


            <GraphicalView
              ref={this.setWrapperRef}
              showIsland={this.props.isIsland}
              canShowWiring={
                this.canShowM172Wiring() || this.canShowPacdriveWiring()
              }
              renderPowerSupplyBuses={this.state.renderPowerSupplyBuses}
            >
              {config.starterType === possible_starter_selections.PLC_IO &&
                config.rangeInfo?.maxComModules > 0 && (lastComModule == null || !lastComModule.isLastModule) &&
                this.renderAddComButton(maxModuleHeight, isSaved)}
              {this.renderByBlocks(maxModuleHeight)}
              {config.rangeInfo?.maxTotalIOModules > 0 &&
                this.renderAddIOButton(maxModuleHeight, isSaved)}
            </GraphicalView>
          </GraphicalViewWrapper>
          <ConfigurationInformationsContainer>
            <ConfigurationInformations
              configuration={config}
              onConsumptionDisplayChange={(showBuses) => {
                this.setState({ renderPowerSupplyBuses: showBuses });
              }}
              onRenderDimensionsChange={(showDimensions) => {
                this.setState({ renderDimensions: showDimensions });
              }}
            ></ConfigurationInformations>
          </ConfigurationInformationsContainer>
        </ConfigurationContainer>
        {hasSelectedModule && (
          <ModuleSettings
            module={selectedModule}
            rangeInfo={config.rangeInfo}
            cartridges={config.cartridges}
            cartridgeMaxQuantity={config.headModule.maxCartridges}
            hasCartridges={this.hasCartridges}
            hasConnectionType={this.hasConnectionType}
            hasFuse={this.hasFuse}
            hasSlottedAdditionalDevices={this.hasSlottedAdditionalDevices}
            canHandleCartridgeActions={this.canHandleCartridgeActions}
            canShowScreenAccessories={this.canShowScreenAccessories}
            canShowSparePartAccessories={this.canShowSparePartAccessories}
            onAddCartridgeRequested={(slot) => {
              this.handleDuplicateConfiguration(
                isSaved,
                () => {
                  this.props.openModal({
                    modalType: "insert_module",
                    modalProps: {
                      insertionSlot: slot,
                      incrementSlot: true,
                      moduleType: configurationModuleType.cartridges,
                      questionType: configurationModuleType.cartridges,
                      onInsert: (mod) => {
                        this.props.addCartridge(mod, slot);
                      },
                      showWarning: false,
                    },
                  });
                },
                true
              );
            }}
            onRemoveCartridgeRequested={this.handleRemoveCartridgeRequested}
            onReplaceModuleRequested={this.handleReplaceRequested}
            onAdjustRequested={this.props.onAdjustRequested}
            onInsertModuleLeftRequested={this.onInsertModuleLeftRequested}
            onInsertModuleRightRequested={this.onInsertModuleRightRequested}
            canDuplicate={this.canDuplicate}
            onDuplicate={this.handleDuplicate}
            canRemove={this.canRemove}
            canAdjust={this.canAdjust}
            canInsertLeft={this.canInsertLeft}
            canInsertRight={this.canInsertRight}
            canReplace={this.canReplace}
            onReplace={this.handleReplace}
            onUpdate={this.handleUpdate}
            onSlottedAdditionalDeviceChange={
              this.handleSlottedAdditionalDeviceChange
            }
            canShowSoftwareVersion={this.canShowSoftwareVersion}
            onSoftwareVersionChange={this.handleSoftwareVersionChange}
            onRemove={this.handleRemove}
            onClose={() => {
              this.updateSelectedModule(null, null);
            }}
            isLoading={this.props.isLoading}
            moduleActionsLoading={this.props.moduleActionsLoading}
            selectedModuleCandidateAccessories={
              this.state.selectedModuleCandidateAccessories
            }
            preferredProductReference={
              appConfiguration.preferredProductReference
            }
            accessoriesLoading={this.props.accessoriesLoading}
            starterType={this.props.configuration.starterType}
          ></ModuleSettings>
        )}
      </Container>
    );
  };
}

export default ConfigurationPreview;
